import { createSelector } from 'reselect';
import type {
  UserSignature,
  SystemSignature,
} from '../reducers/signatures.store';

// Base selector to get signatures state
const getSignaturesState = (state: { signatures: any }) => state.signatures;

// User signatures selectors
export const getUserSignatures = createSelector(
  [getSignaturesState],
  (signatures) => signatures.userSignatures
);

export const getUserSignaturesLoading = createSelector(
  [getSignaturesState],
  (signatures) => signatures.userSignaturesLoading
);

export const getUserSignaturesError = createSelector(
  [getSignaturesState],
  (signatures) => signatures.userSignaturesError
);

export const getDefaultUserSignature = createSelector(
  [getUserSignatures],
  (userSignatures: UserSignature[]) =>
    Array.isArray(userSignatures)
      ? userSignatures.find((sig) => sig.isDefault)
      : undefined
);

export const getActiveUserSignatures = createSelector(
  [getUserSignatures],
  (userSignatures: UserSignature[]) =>
    Array.isArray(userSignatures) ? userSignatures : []
);

// System signatures selectors
export const getSystemSignatures = createSelector(
  [getSignaturesState],
  (signatures) => signatures.systemSignatures
);

export const getSystemSignaturesLoading = createSelector(
  [getSignaturesState],
  (signatures) => signatures.systemSignaturesLoading
);

export const getSystemSignaturesError = createSelector(
  [getSignaturesState],
  (signatures) => signatures.systemSignaturesError
);

export const getDefaultSystemSignature = createSelector(
  [getSystemSignatures],
  (systemSignatures: SystemSignature[]) =>
    Array.isArray(systemSignatures)
      ? systemSignatures.find((sig) => sig.isDefault)
      : undefined
);

export const getActiveSystemSignatures = createSelector(
  [getSystemSignatures],
  (systemSignatures: SystemSignature[]) =>
    Array.isArray(systemSignatures) ? systemSignatures : []
);

// UI state selectors
export const getSelectedSignatureId = createSelector(
  [getSignaturesState],
  (signatures) => signatures.selectedSignatureId
);

export const getSignatureOperationLoading = createSelector(
  [getSignaturesState],
  (signatures) =>
    signatures.creating || signatures.updating || signatures.deleting
);

export const getSignatureError = createSelector(
  [getSignaturesState],
  (signatures) => signatures.error
);

// Combined selectors
export const getSignatureById = createSelector(
  [
    getUserSignatures,
    getSystemSignatures,
    (state: { signatures: any }, signatureId: string) => signatureId,
  ],
  (
    userSignatures: UserSignature[],
    systemSignatures: SystemSignature[],
    signatureId: string
  ) => {
    const allSignatures = [
      ...(Array.isArray(userSignatures) ? userSignatures : []),
      ...(Array.isArray(systemSignatures) ? systemSignatures : []),
    ];
    return allSignatures.find((sig) => sig.id === signatureId);
  }
);

export const getAvailableSignaturesForEmail = createSelector(
  [
    getUserSignatures,
    getSystemSignatures,
    (state: { signatures: any }, isTeamCowbell: boolean) => isTeamCowbell,
  ],
  (
    userSignatures: UserSignature[],
    systemSignatures: SystemSignature[],
    isTeamCowbell: boolean
  ) => {
    if (isTeamCowbell) {
      return Array.isArray(systemSignatures) ? systemSignatures : [];
    }
    return Array.isArray(userSignatures) ? userSignatures : [];
  }
);

export const getDefaultSignatureForEmail = createSelector(
  [
    getDefaultUserSignature,
    getDefaultSystemSignature,
    (state: { signatures: any }, isTeamCowbell: boolean) => isTeamCowbell,
  ],
  (
    defaultUserSignature: UserSignature | undefined,
    defaultSystemSignature: SystemSignature | undefined,
    isTeamCowbell: boolean
  ) => {
    if (isTeamCowbell) {
      return defaultSystemSignature;
    }
    return defaultUserSignature;
  }
);
